import { useEffect, useState } from 'react';
import { logger } from '../utils/logger';
import { Link } from 'react-router-dom';
import { ListMusic, Plus, Play, Trash2, Music, Edit2, Check, X } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { usePlayer } from '../context/PlayerContext';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function PlaylistsPage() {
  const { isAuthenticated } = useAuth();
  const { playTrack } = usePlayer();
  const [playlists, setPlaylists] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [playlistTracks, setPlaylistTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newPlaylist, setNewPlaylist] = useState({ name: '', description: '' });
  const [editingPlaylist, setEditingPlaylist] = useState(null);

  useEffect(() => {
    if (isAuthenticated) {
      fetchPlaylists();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const fetchPlaylists = async () => {
    try {
      const response = await axios.get(`${API_URL}/playlists`);
      setPlaylists(response.data);
    } catch (error) {
      logger.error('Failed to fetch playlists:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlaylistTracks = async (playlist) => {
    setSelectedPlaylist(playlist);
    try {
      const response = await axios.get(`${API_URL}/playlists/${playlist.id}`);
      setPlaylistTracks(response.data.tracks || []);
    } catch (error) {
      logger.error('Failed to fetch playlist tracks:', error);
      setPlaylistTracks([]);
    }
  };

  const createPlaylist = async () => {
    if (!newPlaylist.name.trim()) {
      toast.error('Please enter a playlist name');
      return;
    }
    try {
      await axios.post(`${API_URL}/playlists`, newPlaylist);
      toast.success('Playlist created!');
      setShowCreate(false);
      setNewPlaylist({ name: '', description: '' });
      fetchPlaylists();
    } catch (error) {
      toast.error('Failed to create playlist');
    }
  };

  const updatePlaylist = async () => {
    if (!editingPlaylist) return;
    try {
      await axios.put(`${API_URL}/playlists/${editingPlaylist.id}`, {
        name: editingPlaylist.name,
        description: editingPlaylist.description
      });
      toast.success('Playlist updated');
      setEditingPlaylist(null);
      fetchPlaylists();
      if (selectedPlaylist?.id === editingPlaylist.id) {
        setSelectedPlaylist(editingPlaylist);
      }
    } catch (error) {
      toast.error('Failed to update playlist');
    }
  };

  const deletePlaylist = async (playlistId) => {
    if (!window.confirm('Delete this playlist?')) return;
    try {
      await axios.delete(`${API_URL}/playlists/${playlistId}`);
      toast.success('Playlist deleted');
      if (selectedPlaylist?.id === playlistId) {
        setSelectedPlaylist(null);
        setPlaylistTracks([]);
      }
      fetchPlaylists();
    } catch (error) {
      toast.error('Failed to delete playlist');
    }
  };

  const removeTrackFromPlaylist = async (trackId) => {
    if (!selectedPlaylist) return;
    try {
      await axios.delete(`${API_URL}/playlists/${selectedPlaylist.id}/tracks/${trackId}`);
      toast.success('Track removed');
      fetchPlaylistTracks(selectedPlaylist);
    } catch (error) {
      toast.error('Failed to remove track');
    }
  };

  const playAllTracks = () => {
    if (playlistTracks.length > 0) {
      playTrack(playlistTracks[0], playlistTracks);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="main-content pt-24">
        <div className="container py-12">
          <div className="empty-state glass-card py-16">
            <ListMusic className="empty-state-icon w-16 h-16" />
            <h3 className="text-xl font-semibold mb-2">Sign in to create playlists</h3>
            <p className="text-gray-400 mb-6">Create and manage your own music collections</p>
            <Link to="/login" className="btn-primary">Sign In</Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="main-content pt-24" data-testid="playlists-page">
      <div className="container py-12">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center">
              <ListMusic className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">My Playlists</h1>
              <p className="text-gray-400">{playlists.length} playlists</p>
            </div>
          </div>
          
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <button className="btn-primary flex items-center gap-2" data-testid="create-playlist-btn">
                <Plus className="w-4 h-4" /> New Playlist
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#0a0a0a] border-white/10">
              <DialogHeader>
                <DialogTitle>Create Playlist</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <label className="form-label">Name</label>
                  <Input
                    value={newPlaylist.name}
                    onChange={(e) => setNewPlaylist({ ...newPlaylist, name: e.target.value })}
                    className="input-dark mt-1"
                    placeholder="My Awesome Playlist"
                    data-testid="playlist-name-input"
                  />
                </div>
                <div>
                  <label className="form-label">Description (optional)</label>
                  <textarea
                    value={newPlaylist.description}
                    onChange={(e) => setNewPlaylist({ ...newPlaylist, description: e.target.value })}
                    className="input-dark mt-1 w-full h-20 resize-none"
                    placeholder="A collection of my favorite tracks..."
                  />
                </div>
                <button onClick={createPlaylist} className="btn-primary w-full" data-testid="create-playlist-submit">
                  Create Playlist
                </button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Playlists List */}
          <div className="space-y-4">
            {playlists.length === 0 ? (
              <div className="glass-card p-8 text-center">
                <ListMusic className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                <p className="text-gray-400">No playlists yet</p>
                <p className="text-sm text-gray-500">Create your first playlist!</p>
              </div>
            ) : (
              playlists.map(playlist => (
                <div
                  key={playlist.id}
                  className={`glass-card p-4 cursor-pointer transition-all ${
                    selectedPlaylist?.id === playlist.id ? 'border-purple-500 neon-glow' : 'hover:border-white/20'
                  }`}
                  onClick={() => fetchPlaylistTracks(playlist)}
                  data-testid={`playlist-${playlist.id}`}
                >
                  {editingPlaylist?.id === playlist.id ? (
                    <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                      <Input
                        value={editingPlaylist.name}
                        onChange={(e) => setEditingPlaylist({ ...editingPlaylist, name: e.target.value })}
                        className="input-dark"
                      />
                      <div className="flex gap-2">
                        <button onClick={() => setEditingPlaylist(null)} className="btn-ghost p-2">
                          <X className="w-4 h-4" />
                        </button>
                        <button onClick={updatePlaylist} className="btn-primary p-2">
                          <Check className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold">{playlist.name}</h3>
                        <p className="text-sm text-gray-400">{playlist.track_ids?.length || 0} tracks</p>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); setEditingPlaylist({ ...playlist }); }}
                          className="btn-ghost p-2"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); deletePlaylist(playlist.id); }}
                          className="btn-ghost p-2 text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Playlist Detail */}
          <div className="lg:col-span-2">
            {selectedPlaylist ? (
              <div className="glass-card p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold">{selectedPlaylist.name}</h2>
                    {selectedPlaylist.description && (
                      <p className="text-gray-400 mt-1">{selectedPlaylist.description}</p>
                    )}
                    <p className="text-sm text-gray-500 mt-2">{playlistTracks.length} tracks</p>
                  </div>
                  {playlistTracks.length > 0 && (
                    <button onClick={playAllTracks} className="btn-primary flex items-center gap-2">
                      <Play className="w-4 h-4" /> Play All
                    </button>
                  )}
                </div>

                {playlistTracks.length === 0 ? (
                  <div className="text-center py-12">
                    <Music className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                    <p className="text-gray-400">No tracks in this playlist</p>
                    <p className="text-sm text-gray-500">Add tracks from the music page</p>
                    <Link to="/music" className="btn-secondary mt-4 inline-block">Browse Music</Link>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {playlistTracks.map((track, index) => (
                      <div
                        key={track.id}
                        className="flex items-center gap-4 p-3 rounded-lg hover:bg-white/5 group"
                        data-testid={`playlist-track-${track.id}`}
                      >
                        <span className="w-6 text-center text-gray-500">{index + 1}</span>
                        <img src={track.cover_url} alt={track.title} className="w-12 h-12 rounded object-cover" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{track.title}</p>
                          <p className="text-sm text-gray-400 truncate">{track.album}</p>
                        </div>
                        <button
                          onClick={() => playTrack(track, playlistTracks)}
                          className="btn-ghost p-2 opacity-0 group-hover:opacity-100"
                        >
                          <Play className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => removeTrackFromPlaylist(track.id)}
                          className="btn-ghost p-2 text-red-400 opacity-0 group-hover:opacity-100"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="glass-card p-12 text-center">
                <ListMusic className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                <p className="text-gray-400">Select a playlist to view tracks</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
